import React, { useState, useCallback, useMemo } from "react";

// Utils
import areObjectsEqual from "../../../../utils/areObjectsEqual";

// Context
import { useError } from "../../../../context/ErrorContextProvider";

// Constants
const MAX_CHAR_LIMIT = 100;
const MAX_CHAR_LIMIT_24 = 24;
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const INITIAL_ERRORS = {
  artistName: false,
  email: false,
  emailText: "",
  checkName: false,
};

const INITIAL_CHAR_LIMITS = {
  name: false,
  email: false,
  address: false,
};

// API Service
const artistAPI = {
  checkNameUnique: async (name, wallet) => {
    const encodedName = encodeURIComponent(name);
    const res = await fetch(`/api/artist/checkname/${encodedName}/${wallet}`);
    if (res.status === 200) {
      const data = await res.json();
      return data.checkStatus;
    }
    return false;
  },
};

// Validation helpers
const validateEmail = (email) => {
  return email.toLowerCase().match(EMAIL_REGEX);
};

const sanitizeArtistName = (name) => {
  return name.replace(/\//g, "");
};


const Form = ({
  handleSave,
  formData,
  setFormData,
  copyData,
  account,
  isWalletConnected,
}) => {
  const { showError } = useError();

  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [charLimits, setCharLimits] = useState(INITIAL_CHAR_LIMITS);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return areObjectsEqual(copyData, formData);
  }, [copyData, formData]);

  // Check if artist name is unique
  const checkNameUnique = useCallback(async (name, wallet) => {
    const isNameTaken = await artistAPI.checkNameUnique(name, wallet);
    setErrors(prev => ({ ...prev, checkName: isNameTaken }));
    return isNameTaken;
  }, []);

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    switch (name) {
      case "artist_name": {
        const sanitizedValue = sanitizeArtistName(value);
        if (sanitizedValue.length <= MAX_CHAR_LIMIT) {
          setCharLimits(prev => ({ ...prev, name: sanitizedValue.length === MAX_CHAR_LIMIT_24 }));
          setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
        }
        break;
      }

      case "artist_email": {
        if (value.length <= MAX_CHAR_LIMIT) {
          setCharLimits(prev => ({ ...prev, email: value.length === MAX_CHAR_LIMIT }));
          setFormData(prev => ({ ...prev, [name]: value }));
        }
        break;
      }

      case "payment_address": {
        if (value.length <= MAX_CHAR_LIMIT) {
          setCharLimits(prev => ({ ...prev, address: value.length === MAX_CHAR_LIMIT }));
          setFormData(prev => ({ ...prev, [name]: value }));
        }
        break;
      }

      default:
        break;
    }
  }, [setFormData]);

  // Validate form data
  const validateForm = useCallback(() => {
    const newErrors = { ...INITIAL_ERRORS };

    // Validate artist name
    if (!formData.artist_name) {
      newErrors.artistName = true;
      setErrors(newErrors);
      return false;
    }

    // Validate email
    if (!formData.artist_email) {
      newErrors.email = true;
      newErrors.emailText = "Email is required";
      setErrors(newErrors);
      return false;
    }

    if (!validateEmail(formData.artist_email)) {
      newErrors.email = true;
      newErrors.emailText = "Invalid Email ID";
      setErrors(newErrors);
      return false;
    }

    setErrors(newErrors);
    return true;
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!isWalletConnected) {
      showError("Connect your wallet");
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check if name is unique
    const isNameTaken = await checkNameUnique(formData.artist_name, account);
    if (isNameTaken) {
      return;
    }

    // Submit form
    handleSave(formData);
  }, [isWalletConnected, validateForm, checkNameUnique, formData, account, handleSave, showError]);

  // Determine if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    return !isWalletConnected || hasUnsavedChanges;
  }, [isWalletConnected, hasUnsavedChanges]);


  return (
    <>
      <div>
        {/* Artist Name Input */}
        <label htmlFor="artist_name" className="form-labels">
          Artist Name
        </label>
        <input
          type="text"
          name="artist_name"
          id="artist_name"
          onChange={handleChange}
          value={formData.artist_name}
          className="form-control inputs"
          disabled={!isWalletConnected}
          maxLength={MAX_CHAR_LIMIT_24}
        />
        {errors.artistName && (
          <span className="form-text text-danger">
            Artist name is required
          </span>
        )}
        {errors.checkName && (
          <span className="form-text text-danger">
            This Artist Name already exists, please use a different one.
          </span>
        )}
        {charLimits.name && (
          <span className="form-text text-danger">
            Maximum character limit reached
          </span>
        )}
      </div>

      {/* Email Input */}
      <div className="mt-5">
        <label htmlFor="artist_email" className="form-labels">
          E-mail
        </label>
        <input
          type="email"
          name="artist_email"
          id="artist_email"
          onChange={handleChange}
          value={formData.artist_email}
          className="form-control mt-2 inputs"
          disabled={!isWalletConnected}
          maxLength={MAX_CHAR_LIMIT}
        />
        {errors.email && (
          <span className="form-text text-danger">
            {errors.emailText}
          </span>
        )}
        {charLimits.email && (
          <span className="form-text text-danger">
            Maximum character limit reached
          </span>
        )}
      </div>

      {/* Payment Address Input */}
      <div className="mt-5">
        <label htmlFor="payment_address" className="text-gray form-labels">
          Payment Address
        </label>
        <input
          type="text"
          name="payment_address"
          id="payment_address"
          onChange={handleChange}
          value={formData.payment_address}
          className="form-control inputs"
          disabled={!isWalletConnected}
          maxLength={MAX_CHAR_LIMIT}
        />
        {charLimits.address && (
          <span className="form-text text-danger">
            Maximum character limit reached
          </span>
        )}
      </div>

      {/* Submit Button */}
      <div className="pt-5">
        <input
          type="submit"
          value="Save Changes"
          className="btn bg-orange form-btn"
          onClick={handleSubmit}
          disabled={isSaveDisabled}
        />
      </div>
    </>
  );
};

export default Form;
